import React, { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Save,
  Eye,
  Loader2,
  AlertCircle,
  CheckCircle,
  ShieldCheck,
  ShieldX,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { feedWatchService } from '../../services/feedWatch';
import type { FilterRule, FilterConfig, FilterPreview, FilterOperator } from '../../services/feedWatch';

// ── Constants ───────────────────────────────────────────────────

const FILTER_OPERATORS: { value: FilterOperator; label: string }[] = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'not_contains', label: 'Not Contains' },
  { value: 'starts_with', label: 'Starts With' },
  { value: 'ends_with', label: 'Ends With' },
  { value: 'is_not_empty', label: 'Is Not Empty' },
  { value: 'is_empty', label: 'Is Empty' },
  { value: 'is_greater_than', label: 'Greater Than' },
  { value: 'is_less_than', label: 'Less Than' },
];

const NO_VALUE_OPERATORS = new Set<FilterOperator>(['is_not_empty', 'is_empty']);

// ── Rule Row ────────────────────────────────────────────────────

interface FilterRuleRowProps {
  rule: FilterRule;
  index: number;
  detectedFields: string[];
  onChange: (rule: FilterRule) => void;
  onDelete: () => void;
}

function FilterRuleRow({ rule, index, detectedFields, onChange, onDelete }: FilterRuleRowProps) {
  return (
    <div className="group flex items-center gap-3 p-3 rounded-xl bg-muted/30 hover:bg-muted/50 transition-colors duration-150">
      {/* Logic badge */}
      <div className="w-14 shrink-0">
        {index > 0 ? (
          <Select
            value={rule.logic}
            onValueChange={(v) => onChange({ ...rule, logic: v as 'AND' | 'OR' })}
          >
            <SelectTrigger className="h-9 w-14 rounded-lg border-2 text-xs font-semibold focus:border-primary focus:ring-2 focus:ring-primary/20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">AND</SelectItem>
              <SelectItem value="OR">OR</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider pl-2">
            Where
          </span>
        )}
      </div>

      {/* Field */}
      <Select
        value={rule.field || '_none'}
        onValueChange={(v) => onChange({ ...rule, field: v === '_none' ? '' : v })}
      >
        <SelectTrigger className="h-10 min-w-[160px] flex-1 rounded-xl border-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20">
          <SelectValue placeholder="Select field..." />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_none" disabled>Select field...</SelectItem>
          {detectedFields.map((f) => (
            <SelectItem key={f} value={f}>{f}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Operator */}
      <Select
        value={rule.operator}
        onValueChange={(v) => onChange({ ...rule, operator: v as FilterOperator })}
      >
        <SelectTrigger className="h-10 min-w-[150px] rounded-xl border-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {FILTER_OPERATORS.map((op) => (
            <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Value */}
      {!NO_VALUE_OPERATORS.has(rule.operator) && (
        <Input
          value={rule.value || ''}
          onChange={(e) => onChange({ ...rule, value: e.target.value })}
          placeholder="Value..."
          className="h-10 flex-1 min-w-[120px] rounded-xl border-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
      )}

      {/* Delete */}
      <button
        onClick={onDelete}
        className="p-2 rounded-lg text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:text-destructive hover:bg-destructive/10 transition-all duration-150"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── Filter Section ──────────────────────────────────────────────

interface FilterSectionProps {
  title: string;
  description: string;
  type: 'include' | 'exclude';
  rules: FilterRule[];
  detectedFields: string[];
  onChange: (rules: FilterRule[]) => void;
}

function FilterSection({ title, description, type, rules, detectedFields, onChange }: FilterSectionProps) {
  const addRule = () => {
    onChange([
      ...rules,
      { field: '', operator: 'equals', value: '', logic: 'AND' },
    ]);
  };

  const updateRule = (index: number, updated: FilterRule) => {
    onChange(rules.map((r, i) => (i === index ? updated : r)));
  };

  const deleteRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const Icon = type === 'include' ? ShieldCheck : ShieldX;

  return (
    <Card className="border-2 rounded-xl overflow-hidden">
      {/* Section header */}
      <div className="flex items-center gap-3 px-6 py-4 border-b bg-muted/30">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
          type === 'include'
            ? 'bg-green-500/10 text-green-600'
            : 'bg-destructive/10 text-destructive'
        }`}>
          <Icon className="h-4.5 w-4.5" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2.5">
            <span className="text-sm font-semibold">{title}</span>
            <Badge
              variant="outline"
              className={`text-xs ${
                type === 'include'
                  ? 'bg-green-500/10 text-green-600 border-green-500/20'
                  : 'bg-destructive/10 text-destructive border-destructive/20'
              }`}
            >
              {rules.length} rule{rules.length !== 1 ? 's' : ''}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </div>

      {/* Rules */}
      <div className="p-5 space-y-2">
        {rules.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No {type} rules yet. Products will {type === 'include' ? 'all be included' : 'not be excluded'}.</p>
          </div>
        ) : (
          rules.map((rule, idx) => (
            <FilterRuleRow
              key={idx}
              rule={rule}
              index={idx}
              detectedFields={detectedFields}
              onChange={(updated) => updateRule(idx, updated)}
              onDelete={() => deleteRule(idx)}
            />
          ))
        )}

        <Button
          variant="outline"
          size="sm"
          className="mt-3 rounded-lg border-2 border-dashed hover:border-primary hover:bg-primary/5 transition-all duration-200"
          onClick={addRule}
        >
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add Rule
        </Button>
      </div>
    </Card>
  );
}

// ── Main FilterBuilder ──────────────────────────────────────────

interface FilterBuilderProps {
  feedId: string;
  detectedFields: string[];
  onFiltersApplied?: () => void;
}

export function FilterBuilder({ feedId, detectedFields, onFiltersApplied }: FilterBuilderProps) {
  const [includeRules, setIncludeRules] = useState<FilterRule[]>([]);
  const [excludeRules, setExcludeRules] = useState<FilterRule[]>([]);
  const [preview, setPreview] = useState<FilterPreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const config = await feedWatchService.getFilters(feedId);
        setIncludeRules([...config.include]);
        setExcludeRules([...config.exclude]);
      } catch (err: any) {
        if (!err.message.includes('not found')) {
          setError(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [feedId]);

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccessMsg(null);
      await feedWatchService.updateFilters(feedId, {
        include: includeRules,
        exclude: excludeRules,
      });
      setSuccessMsg('Filters saved successfully');
      setTimeout(() => setSuccessMsg(null), 3000);
      onFiltersApplied?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePreview = async () => {
    try {
      setIsPreviewing(true);
      setError(null);
      const result = await feedWatchService.previewFilters(feedId);
      setPreview(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsPreviewing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const totalRules = includeRules.length + excludeRules.length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Toolbar */}
      <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-xl border">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span className="font-semibold text-foreground">{totalRules}</span> rule{totalRules !== 1 ? 's' : ''} configured
        </div>
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          className="rounded-lg"
          onClick={handlePreview}
          disabled={isPreviewing}
        >
          {isPreviewing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />}
          Preview Impact
        </Button>
        <Button
          size="sm"
          className="rounded-lg"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save Filters
        </Button>
      </div>

      {/* Messages */}
      {error && (
        <div className="flex items-center gap-2.5 text-sm text-destructive bg-destructive/10 rounded-xl px-5 py-3.5 border border-destructive/20">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}
      {successMsg && (
        <div className="flex items-center gap-2.5 text-sm text-green-600 bg-green-500/10 rounded-xl px-5 py-3.5 border border-green-500/20">
          <CheckCircle className="h-4 w-4 shrink-0" />
          {successMsg}
        </div>
      )}

      {/* Preview stats */}
      {preview && (
        <Card className="border-2 rounded-xl p-5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Filter Impact Preview</p>
          <div className="grid grid-cols-3 gap-6">
            <div className="text-center p-4 rounded-xl bg-muted/30">
              <p className="text-2xl font-bold tabular-nums">{preview.total_products.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground mt-1">Total Products</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-green-500/5 border border-green-500/10">
              <p className="text-2xl font-bold tabular-nums text-green-600">{preview.included_count.toLocaleString()}</p>
              <p className="text-xs text-green-600/80 mt-1">Included</p>
            </div>
            <div className="text-center p-4 rounded-xl bg-destructive/5 border border-destructive/10">
              <p className="text-2xl font-bold tabular-nums text-destructive">{preview.excluded_count.toLocaleString()}</p>
              <p className="text-xs text-destructive/80 mt-1">Excluded</p>
            </div>
          </div>
        </Card>
      )}

      {/* Include rules */}
      <FilterSection
        title="Include Rules"
        description="Only products matching these rules will be included in exports"
        type="include"
        rules={includeRules}
        detectedFields={detectedFields}
        onChange={setIncludeRules}
      />

      {/* Exclude rules */}
      <FilterSection
        title="Exclude Rules"
        description="Products matching these rules will be removed from exports"
        type="exclude"
        rules={excludeRules}
        detectedFields={detectedFields}
        onChange={setExcludeRules}
      />
    </div>
  );
}
